import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useSEO, generateLocalBusinessSchema, generateServiceSchema } from '../../utils/seo';

export default function ListingCampsBayPage() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');

  // SEO Configuration
  useSEO({
    title: 'Camps Bay Luxury Painters - High-End Residential Painting | Villa Specialists',
    description: 'Luxury painting services in Camps Bay, Clifton, and Bakoven. Specialized in high-end residential properties and luxury villas. Over 17 years serving Atlantic Seaboard with premium finishes.',
    keywords: 'painters Camps Bay, luxury painters Camps Bay, high-end painting, villa painting, residential painting, Atlantic Seaboard painters',
    schema: {
      '@context': 'https://schema.org',
      '@graph': [
        generateLocalBusinessSchema({
          name: 'Painters Camps Bay',
          description: 'Luxury painting services for high-end properties',
          address: 'Camps Bay, Cape Town',
          priceRange: '$$$',
          serviceArea: ['Camps Bay', 'Clifton', 'Bakoven', 'Llandudno', 'Hout Bay']
        }),
        generateServiceSchema({
          name: 'Luxury Painting Services',
          description: 'High-end residential and villa painting',
          provider: 'Painters Camps Bay',
          areaServed: ['Camps Bay', 'Clifton', 'Bakoven'],
          serviceType: ['Luxury Residential Painting', 'Villa Painting', 'High-End Finishes']
        })
      ]
    }
  });

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const listing = {
    name: "Painters Camps Bay",
    location: "Camps Bay, Cape Town",
    website: "https://painterscampsbay.co.za",
    heroImage: "https://public.readdy.ai/ai/img_res/66556b1c7e4cc45f2fe6f9e8145509f5.jpg",
    gallery: [
      "https://readdy.ai/api/search-image?query=stunning%20luxury%20villa%20exterior%20with%20fresh%20white%20paint%20modern%20architecture%20ocean%20views%20and%20mountain%20backdrop%20professional%20finish&width=800&height=600&seq=gallery-campsbay1&orientation=landscape",
      "https://readdy.ai/api/search-image?query=elegant%20interior%20painting%20in%20luxury%20home%20with%20high%20ceilings%20designer%20finishes%20and%20ocean%20views%20through%20large%20windows&width=800&height=600&seq=gallery-campsbay2&orientation=landscape",
      "https://readdy.ai/api/search-image?query=coastal%20property%20exterior%20painting%20with%20weather%20resistant%20coating%20modern%20villa%20with%20pool%20and%20ocean%20backdrop&width=800&height=600&seq=gallery-campsbay3&orientation=landscape",
      "https://readdy.ai/api/search-image?query=luxury%20home%20interior%20with%20designer%20paint%20finishes%20feature%20walls%20and%20contemporary%20styling%20professional%20craftsmanship&width=800&height=600&seq=gallery-campsbay4&orientation=landscape"
    ],
    services: [
      {
        title: "Luxury Villa Painting",
        description: "Specialized painting services for high-end luxury villas and beachfront properties. We understand the unique requirements of premium homes and deliver finishes that match the sophistication of your property.",
        features: ["Premium paint systems", "Designer color consultation", "High-end finishes", "Luxury property specialists"]
      },
      {
        title: "Coastal Protection Coating",
        description: "Advanced coating systems specifically designed for coastal properties. Our marine-grade paints and protective coatings provide superior resistance against salt air, wind, and moisture.",
        features: ["Marine-grade coatings", "Salt air protection", "UV resistance", "Weather-resistant systems"]
      },
      {
        title: "Designer Interior Finishes",
        description: "Collaborate with interior designers to create stunning custom finishes. From feature walls to specialty textures, we bring designer visions to life with precision and artistry.",
        features: ["Custom color matching", "Specialty finishes", "Feature walls", "Designer collaboration"]
      }
    ],
    serviceAreas: ["Camps Bay", "Clifton", "Bakoven", "Bantry Bay", "Fresnaye", "Sea Point"],
    about: "Painters Camps Bay is the premier choice for luxury property painting in Cape Town's most exclusive coastal suburb. With over 14 years of experience serving high-end properties, we have established ourselves as the trusted painting contractor for discerning homeowners who demand nothing but the best. Our team specializes in luxury villas, beachfront properties, and designer homes.",
    aboutExtended: [
      "Located in the heart of Camps Bay, we understand the unique challenges and requirements of painting coastal luxury properties. The combination of salt air, strong winds, and intense UV exposure requires specialized knowledge and premium materials. We use only the finest marine-grade paints and coating systems, ensuring that your property not only looks stunning but is also protected against the harsh coastal environment.",
      "Our painters are highly skilled craftsmen who have worked on some of Cape Town's most prestigious properties. We regularly collaborate with leading interior designers and architects, bringing their visions to life with precision and attention to detail. Whether it's a contemporary beachfront villa or a classic Mediterranean-style home, we have the expertise to deliver flawless results.",
      "What sets us apart is our commitment to excellence in every aspect of our service. From the initial consultation to the final inspection, we maintain the highest standards of professionalism and quality. We understand that luxury properties require discretion, minimal disruption, and impeccable results. Our team works efficiently and respectfully, ensuring that your home remains a sanctuary throughout the painting process."
    ],
    ourApproach: [
      "Comprehensive luxury property assessment",
      "Premium marine-grade materials selection",
      "Meticulous surface preparation",
      "Designer color consultation",
      "Expert application techniques",
      "Quality assurance and final inspection"
    ],
    whyChoose: [
      "14+ years luxury property experience",
      "Coastal painting specialists",
      "Premium materials only",
      "Designer collaboration",
      "Fully licensed and insured",
      "Satisfaction guaranteed"
    ],
    testimonials: [
      {
        name: "Alexandra Thornton",
        rating: 5,
        text: "Exceptional service! The team transformed our beachfront villa beautifully. They understood the coastal challenges and used premium materials. The finish is absolutely flawless."
      },
      {
        name: "David Goldstein",
        rating: 5,
        text: "We've used several painting companies over the years, but Painters Camps Bay is by far the best. Professional, reliable, and the quality of work is outstanding. Highly recommended for luxury properties."
      },
      {
        name: "Catherine van der Westhuizen",
        rating: 5,
        text: "They worked closely with our interior designer to achieve the perfect finishes throughout our home. The attention to detail and craftsmanship is remarkable. Worth every penny!"
      }
    ]
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className={`sticky top-0 z-50 border-b border-gray-800 transition-all duration-300 ${isScrolled ? 'bg-black text-white' : 'bg-black text-white'}`}>
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="text-3xl font-bold whitespace-nowrap">
              <span className="text-white">PAINTE</span>
              <span className="text-[#e9f638]">RRR</span>
            </Link>
            <nav className="hidden md:flex items-center space-x-8">
              <Link to="/" className="hover:text-[#e9f638] transition whitespace-nowrap">Home</Link>
              <Link to="/about" className="hover:text-[#e9f638] transition whitespace-nowrap">About</Link>
              <Link to="/contact" className="bg-[#e9f638] text-black px-6 py-2 rounded-lg font-semibold hover:bg-white transition whitespace-nowrap cursor-pointer">Get a Quote</Link>
            </nav>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <div className="relative h-96 w-full overflow-hidden">
        <img 
          src={listing.heroImage}
          alt={listing.name}
          className="w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent"></div>
        <div className="absolute bottom-0 left-0 right-0 container mx-auto px-4 pb-8">
          <h1 className="text-5xl font-bold text-white mb-2">{listing.name}</h1>
          <p className="text-xl text-gray-200 flex items-center">
            <i className="ri-map-pin-line mr-2"></i>
            {listing.location}
          </p>
        </div>
      </div>

      {/* Contact Bar */}
      <div className="bg-[#e9f638] py-4">
        <div className="container mx-auto px-4">
          <div className="flex flex-col md:flex-row justify-center items-center gap-4">
            <a 
              href={listing.website}
              target="_blank"
              rel="noopener noreferrer"
              className="bg-black text-white px-8 py-3 rounded-lg font-bold hover:bg-gray-800 transition whitespace-nowrap cursor-pointer"
            >
              Visit Website
            </a>
          </div>
        </div>
      </div>

      {/* Tabs Navigation */}
      <div className="bg-gray-50 border-b border-gray-200 sticky top-[73px] z-40">
        <div className="container mx-auto px-4">
          <div className="flex space-x-8 overflow-x-auto">
            <button
              onClick={() => setActiveTab('overview')}
              className={`py-4 px-2 font-semibold whitespace-nowrap border-b-2 transition cursor-pointer ${
                activeTab === 'overview' ? 'border-[#e9f638] text-black' : 'border-transparent text-gray-500 hover:text-black'
              }`}
            >
              Overview
            </button>
            <button
              onClick={() => setActiveTab('services')}
              className={`py-4 px-2 font-semibold whitespace-nowrap border-b-2 transition cursor-pointer ${
                activeTab === 'services' ? 'border-[#e9f638] text-black' : 'border-transparent text-gray-500 hover:text-black'
              }`}
            >
              Services
            </button>
            <button
              onClick={() => setActiveTab('gallery')}
              className={`py-4 px-2 font-semibold whitespace-nowrap border-b-2 transition cursor-pointer ${
                activeTab === 'gallery' ? 'border-[#e9f638] text-black' : 'border-transparent text-gray-500 hover:text-black'
              }`}
            >
              Gallery
            </button>
            <button
              onClick={() => setActiveTab('reviews')}
              className={`py-4 px-2 font-semibold whitespace-nowrap border-b-2 transition cursor-pointer ${
                activeTab === 'reviews' ? 'border-[#e9f638] text-black' : 'border-transparent text-gray-500 hover:text-black'
              }`}
            >
              Reviews
            </button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="container mx-auto px-4 py-12">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2">
            {activeTab === 'overview' && (
              <div>
                <section className="mb-12">
                  <h2 className="text-3xl font-bold mb-6">About Painters Camps Bay</h2>
                  <p className="text-gray-700 text-lg leading-relaxed mb-4">{listing.about}</p>
                  {listing.aboutExtended.map((paragraph, index) => (
                    <p key={index} className="text-gray-700 text-lg leading-relaxed mb-4">{paragraph}</p>
                  ))}
                </section>

                <section className="mb-12">
                  <h3 className="text-2xl font-bold mb-6">Our Professional Approach</h3>
                  <div className="bg-gray-50 p-6 rounded-lg border-2 border-gray-200">
                    <ul className="space-y-3">
                      {listing.ourApproach.map((step, index) => (
                        <li key={index} className="flex items-start">
                          <i className="ri-checkbox-circle-line text-[#e9f638] text-2xl mr-3 mt-1"></i>
                          <span className="text-gray-700 text-lg">{step}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </section>

                <section className="mb-12">
                  <h3 className="text-2xl font-bold mb-6">Why Choose Painters Camps Bay</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {listing.whyChoose.map((reason, index) => (
                      <div key={index} className="flex items-start">
                        <i className="ri-check-line text-[#e9f638] text-2xl mr-3 mt-1"></i>
                        <span className="text-gray-700 text-lg">{reason}</span>
                      </div>
                    ))}
                  </div>
                </section>

                <section>
                  <h4 className="text-2xl font-bold mb-6">Areas We Serve</h4>
                  <p className="text-gray-700 text-lg mb-4">We proudly serve the following exclusive coastal areas:</p>
                  <div className="flex flex-wrap gap-3">
                    {listing.serviceAreas.map((area, index) => (
                      <span key={index} className="bg-gray-100 text-black px-4 py-2 rounded-full font-semibold">
                        {area}
                      </span>
                    ))}
                  </div>
                </section>
              </div>
            )}

            {activeTab === 'services' && (
              <div>
                <h2 className="text-3xl font-bold mb-4">Our Professional Painting Services</h2>
                <p className="text-gray-700 text-lg mb-8">We offer specialized painting services for luxury coastal properties in Camps Bay and surrounding areas.</p>
                <div className="space-y-8">
                  {listing.services.map((service, index) => (
                    <div key={index} className="bg-gray-50 p-6 rounded-lg border-2 border-gray-200 hover:border-[#e9f638] transition">
                      <h3 className="text-2xl font-bold mb-3">{service.title}</h3>
                      <p className="text-gray-700 mb-4 text-lg">{service.description}</p>
                      <h4 className="font-semibold text-lg mb-3">What We Offer:</h4>
                      <ul className="space-y-2">
                        {service.features.map((feature, idx) => (
                          <li key={idx} className="flex items-center text-gray-700">
                            <i className="ri-arrow-right-s-line text-[#e9f638] mr-2"></i>
                            {feature}
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {activeTab === 'gallery' && (
              <div>
                <h2 className="text-3xl font-bold mb-8">Project Gallery</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {listing.gallery.map((image, index) => (
                    <div key={index} className="h-80 w-full overflow-hidden rounded-lg shadow-lg hover:shadow-2xl transition">
                      <img 
                        src={image}
                        alt={`Project ${index + 1}`}
                        className="w-full h-full object-cover object-top hover:scale-110 transition-transform duration-500 cursor-pointer"
                      />
                    </div>
                  ))}
                </div>
              </div>
            )}

            {activeTab === 'reviews' && (
              <div>
                <h2 className="text-3xl font-bold mb-8">Customer Reviews</h2>
                <div className="space-y-6">
                  {listing.testimonials.map((testimonial, index) => (
                    <div key={index} className="bg-gray-50 p-6 rounded-lg border-2 border-gray-200">
                      <div className="flex items-center mb-4">
                        <div className="flex mr-4">
                          {[...Array(testimonial.rating)].map((_, i) => (
                            <i key={i} className="ri-star-fill text-[#e9f638] text-xl"></i>
                          ))}
                        </div>
                        <span className="font-bold text-lg">{testimonial.name}</span>
                      </div>
                      <p className="text-gray-700 text-lg leading-relaxed">{testimonial.text}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Sidebar */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-lg shadow-lg p-6 sticky top-24">
              <h3 className="text-2xl font-bold mb-6">Contact Information</h3>
              
              <div className="space-y-4 mb-6">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 flex items-center justify-center">
                    <i className="ri-map-pin-line text-2xl text-[#e9f638]"></i>
                  </div>
                  <div>
                    <p className="font-semibold">Service Areas</p>
                    <p className="text-gray-600">Camps Bay, Clifton, Bakoven</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 flex items-center justify-center">
                    <i className="ri-time-line text-2xl text-[#e9f638]"></i>
                  </div>
                  <div>
                    <p className="font-semibold">Business Hours</p>
                    <p className="text-gray-600">Mon-Fri: 8AM-5PM</p>
                    <p className="text-gray-600">Sat: 9AM-1PM</p>
                  </div>
                </div>
              </div>
              
              <a 
                href={listing.website}
                target="_blank"
                rel="noopener noreferrer"
                className="block w-full bg-[#e9f638] text-black text-center py-3 rounded-lg font-bold hover:bg-black hover:text-[#e9f638] transition-colors duration-300 whitespace-nowrap cursor-pointer"
              >
                Visit Website
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-black text-white border-t border-gray-800">
        <div className="container mx-auto px-4 py-12">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8 mb-8">
            <div>
              <h3 className="text-2xl font-bold mb-4">
                <span className="text-white">PAINTE</span>
                <span className="text-[#e9f638]">RRR</span>
              </h3>
              <p className="text-gray-400">South Africa's premier directory for professional house painters.</p>
            </div>
            <div>
              <h4 className="font-semibold mb-4 text-[#e9f638]">Quick Links</h4>
              <ul className="space-y-2">
                <li><Link to="/" className="text-gray-400 hover:text-[#e9f638] cursor-pointer">Home</Link></li>
                <li><Link to="/about" className="text-gray-400 hover:text-[#e9f638] cursor-pointer">About</Link></li>
                <li><Link to="/contact" className="text-gray-400 hover:text-[#e9f638] cursor-pointer">Contact</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4 text-[#e9f638]">Service Areas</h4>
              <ul className="space-y-2 text-gray-400">
                <li>Table View</li>
                <li>Melkbosstrand</li>
                <li>Durbanville</li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4 text-[#e9f638]">Connect</h4>
              <p className="text-gray-400">Find the perfect painter for your project today.</p>
            </div>
          </div>
          <div className="border-t border-gray-800 pt-8 flex flex-col md:flex-row justify-between items-center text-sm text-gray-400">
            <p>© 2025 PAINTERRR. All rights reserved.</p>
            <div className="flex space-x-4 mt-4 md:mt-0">
              <a href="https://readdy.ai/?origin=logo" target="_blank" rel="noopener noreferrer" className="hover:text-[#e9f638] cursor-pointer">
                Powered by Readdy
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
